import { useMemo } from "react";

import { RestrictedCountry } from "@/app/components/location-to/type/location-from.type";
import { BrandSpecificParamsType } from "@/app/wizard/wizard-types";
import {
  AnnualMultiTrip,
  Europe,
  EuropeHR,
  EuropeLR,
  IrelandOnly,
  NotSure,
  SingleTrip,
  SpainOnly,
  UKOnly,
  Worldwide,
  WorldwideHR,
  WorldwideLR,
  fcdoCountryList,
} from "@/config/app-constant";
import { PRODUCT, SECONDARY_PRODUCT } from "@/constants";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { QuoteCountryType } from "@/types/AddressTypes";
import { OptionListProps, LocationToProps } from "@/types/LocationToType";
import { STCountryArray } from "@/types/QuestionPayloadType";

export const brand: "staysure" | "avanti" | "expat" =
  SECONDARY_PRODUCT === "expat"
    ? "expat"
    : (PRODUCT as "staysure" | "avanti") ?? "staysure";

export const countryData = [
  {
    code: "UK1",
    displayName: "United Kingdom",
    description: "United Kingdom",
  },
  {
    code: "UK3",
    displayName: "Isle of Man",
    description: "Isle of Man",
  },
  {
    code: "UK4",
    displayName: "Guernsey",
    description: "Guernsey",
  },
  {
    code: "UK5",
    displayName: "Jersey",
    description: "Jersey",
  },
  {
    code: "spain",
    displayName: "Spain",
    description: "Spain",
  },
  {
    code: "france",
    displayName: "France",
    description: "France",
  },
  {
    code: "portugal",
    displayName: "Portugal",
    description: "Portugal",
  },
];

export const defaultFCDOCountries = [
  {
    countryCode: "NE",
    warning: false,
  },
  {
    countryCode: "SO",
    warning: false,
  },
  {
    countryCode: "TD",
    warning: false,
  },
  {
    countryCode: "YE",
    warning: false,
  },
  {
    countryCode: "CF",
    warning: false,
  },
  {
    countryCode: "ML",
    warning: false,
  },
  {
    countryCode: "AF",
    warning: false,
  },
  {
    countryCode: "IQ",
    warning: false,
  },
  {
    countryCode: "LY",
    warning: false,
  },
  {
    countryCode: "SY",
    warning: false,
  },
  {
    countryCode: "BF",
    warning: false,
  },
  {
    countryCode: "IR",
    warning: false,
  },
  {
    countryCode: "UA",
    warning: false,
  },
  {
    countryCode: "BY",
    warning: false,
  },
  {
    countryCode: "HT",
    warning: false,
  },
  {
    countryCode: "SD",
    warning: false,
  },
  {
    countryCode: "AN",
    warning: false,
  },
  {
    countryCode: "RU",
    warning: false,
  },
  {
    countryCode: "LB",
    warning: false,
  },
  {
    countryCode: "IL",
    warning: false,
  },
];

export function getSelectedLocationToSupport(
  toLocation: string | undefined,
  prop: LocationToProps,
  toLocationText: string | undefined,
  policyType: string
) {
  let drawerObj: {
    drawerContent: object | null | undefined;
    drawerEnable: boolean | undefined;
    rightContent: object | null | undefined;
    loadMoreButtonText: string | undefined;
    chatButtonLabel: string | undefined;
  } = {
    drawerContent: null,
    drawerEnable: false,
    rightContent: null,
    loadMoreButtonText: "",
    chatButtonLabel: "",
  };
  if (toLocation === UKOnly && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.ukOnlyAMTSupportDrawer,
      drawerEnable: prop?.ukOnlyAMTLoadMoreButton,
      rightContent: prop?.ukOnlyAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (toLocation === IrelandOnly && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.ukOnlyAMTSupportDrawer,
      drawerEnable: prop?.ukOnlyAMTLoadMoreButton,
      rightContent: prop?.ukOnlyAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (toLocation === SpainOnly && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.ukOnlyAMTSupportDrawer, // Reuse UK drawer for Spain
      drawerEnable: prop?.ukOnlyAMTLoadMoreButton, // Reuse UK load more button for Spain
      rightContent: prop?.ukOnlyAMTDescription, // Reuse UK description for Spain
      loadMoreButtonText: prop?.loadMoreButtonText, // Reuse UK load more button text for Spain
      chatButtonLabel: prop?.chatButtonLabel, // Reuse UK chat button label for Spain
    };
  }

  if (toLocation === Europe && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.europeAMTSupportDrawer,
      drawerEnable: prop?.europeLoadMoreButton,
      rightContent: prop?.europeAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (toLocation === Worldwide && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.worldWideAMTSupportDrawer,
      drawerEnable: prop?.worldWideLoadMoreButton,
      rightContent: prop?.worldWideAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (toLocation === EuropeHR && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.europeYesAMTSupportDrawer,
      drawerEnable: prop?.europeYesLoadMoreButton,
      rightContent: prop?.europeYesAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (toLocation === EuropeLR && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.europeNoAMTSupportDrawer,
      drawerEnable: prop?.europeNoLoadMoreButton,
      rightContent: prop?.europeNoAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (toLocation === WorldwideHR && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.worldWideYesAMTSupportDrawer,
      drawerEnable: prop?.worldWideYesLoadMoreButton,
      rightContent: prop?.worldWideYesAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (toLocation === WorldwideLR && policyType === AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.worldWideNoAMTSupportDrawer,
      drawerEnable: prop?.worldWideNoLoadMoreButton,
      rightContent: prop?.worldWideNoAMTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }
  if ((toLocation || toLocationText) && policyType !== AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.supportSTDrawer,
      drawerEnable: prop?.supportSTLoadMoreButton,
      rightContent: prop?.assistantSTDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (policyType === AnnualMultiTrip && !toLocation) {
    drawerObj = {
      drawerContent: prop?.amtSupportDrawer,
      drawerEnable: prop?.amtLoadMoreButton,
      rightContent: prop?.amtAssistantDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  if (!toLocation && !toLocationText && policyType !== AnnualMultiTrip) {
    drawerObj = {
      drawerContent: prop?.locationToSupportDrawer,
      drawerEnable: prop?.supportSTLoadMoreButton,
      rightContent: prop?.locationToAssistantDescription,
      loadMoreButtonText: prop?.loadMoreButtonText,
      chatButtonLabel: prop?.chatButtonLabel,
    };
  }

  return drawerObj;
}

export function getSubmitButtonState(
  policyType: string,
  selectedToCountry: string,
  currentDefinition: boolean,
  selectedFromCountry?: string
) {
  let buttonDisable = true;
  if (
    selectedToCountry &&
    selectedToCountry !== "" &&
    selectedToCountry !== Worldwide &&
    selectedToCountry !== Europe
  ) {
    buttonDisable = false;
  }
  if (policyType === AnnualMultiTrip) {
    if (
      selectedToCountry &&
      (selectedToCountry === UKOnly ||
        selectedToCountry === WorldwideHR ||
        selectedToCountry === WorldwideLR ||
        selectedToCountry === EuropeHR ||
        selectedToCountry === EuropeLR)
    ) {
      buttonDisable = false;
    }
  }

  if (currentDefinition && !selectedFromCountry) {
    buttonDisable = true;
  }

  return buttonDisable;
}

export const answersQTwoWhereGoing = [
  {
    id: 1,
    label: "UK only",
    value: "UK",
  },
  {
    id: 1,
    label: "Ireland only",
    value: "IE",
  },
  {
    id: 1,
    label: "Spain only",
    value: "ESP",
  },
  {
    id: 2,
    label: "Europe",
    value: "EUROPE",
  },
  {
    id: 3,
    label: "Worldwide",
    value: "WORLD",
  },
];

// Custom hook to get translated answers array
export function useTranslatedAnswersQTwoWhereGoing() {
  const { t } = useWizardTranslation();

  return useMemo(
    () => [
      {
        id: 1,
        label: t("to_uk", "UK only"),
        value: "UK",
      },
      {
        id: 1,
        label: t("to_ireland", "Ireland only"),
        value: "IE",
      },
      {
        id: 1,
        label: t("spain_only", "Spain only"),
        value: "ESP",
      },
      {
        id: 2,
        label: t("to_europe", "Europe"),
        value: "EUROPE",
      },
      {
        id: 3,
        label: t("to_world_wide", "Worldwide"),
        value: "WORLD",
      },
    ],
    [t]
  );
}

// Custom hook for filtered translated answers
export function useFilteredTranslatedAnswersQTwoWhereGoing(
  currentDefinition?: BrandSpecificParamsType
) {
  const translatedAnswers = useTranslatedAnswersQTwoWhereGoing();
  return useMemo(() => {
    if (Array.isArray(currentDefinition)) {
      return currentDefinition
        .map((val) => translatedAnswers.find((answer) => answer.value === val))
        .filter(Boolean);
    }
    return [];
  }, [translatedAnswers, currentDefinition]);
}

// Custom hook to find translated country by value
export function useFindTranslatedCountryIdByValue(country: string | undefined) {
  const translatedAnswers = useTranslatedAnswersQTwoWhereGoing();
  return useMemo(() => {
    return translatedAnswers.find((answer) => answer.value === country);
  }, [translatedAnswers, country]);
}

// Filter answers based on brand
export function filteredAnswersQTwoWhereGoing(
  currentDefinition?: BrandSpecificParamsType
) {
  if (Array.isArray(currentDefinition)) {
    return currentDefinition
      .map((val) =>
        answersQTwoWhereGoing.find((answer) => answer.value === val)
      )
      .filter(Boolean);
  }
  return [];
}

export function findAnswerById(
  id: number,
  currentDefinition?: BrandSpecificParamsType
) {
  return filteredAnswersQTwoWhereGoing(currentDefinition).find(
    (answer) => answer?.id === id
  );
}

export const answersQTwoEurope = [
  {
    id: 1,
    label: "Yes",
    value: "EUROPE_HR",
  },
  {
    id: 2,
    label: "No",
    value: "EUROPE_LR",
  },
];

// Custom hook to get translated Europe answers
export function useTranslatedAnswersQTwoEurope() {
  const { t } = useWizardTranslation();

  return useMemo(
    () => [
      {
        id: 1,
        label: t("button_group_yes", "Yes"),
        value: "EUROPE_HR",
      },
      {
        id: 2,
        label: t("button_group_no", "No"),
        value: "EUROPE_LR",
      },
    ],
    [t]
  );
}

export function findAnswerForEuropeId(id: number) {
  return answersQTwoEurope.find((answer) => answer.id === id);
}

// Custom hooks for translated finder functions
export function useFindTranslatedIdByEuropeValue(value: string | undefined) {
  const translatedAnswers = useTranslatedAnswersQTwoEurope();
  return useMemo(() => {
    return translatedAnswers.find((answer) => answer.value === value);
  }, [translatedAnswers, value]);
}

export const answersQTwoWorldwide = [
  {
    id: 1,
    label: "Yes",
    value: "WORLD_HR",
  },
  {
    id: 2,
    label: "No",
    value: "WORLD_LR",
  },
];

// Custom hook to get translated Worldwide answers
export function useTranslatedAnswersQTwoWorldwide() {
  const { t } = useWizardTranslation();

  return useMemo(
    () => [
      {
        id: 1,
        label: t("button_group_yes", "Yes"),
        value: "WORLD_HR",
      },
      {
        id: 2,
        label: t("button_group_no", "No"),
        value: "WORLD_LR",
      },
    ],
    [t]
  );
}

export function findAnswerWorldwideById(id: number) {
  return answersQTwoWorldwide.find((answer) => answer.id === id);
}

// Custom hooks for translated finder functions
export function useFindTranslatedIdByWorldWideValue(value: string | undefined) {
  const translatedAnswers = useTranslatedAnswersQTwoWorldwide();
  return useMemo(() => {
    return translatedAnswers.find((answer) => answer.value === value);
  }, [translatedAnswers, value]);
}

export function generateTagSearchArray(countryArray: QuoteCountryType[]) {
  const modifyCountryList: OptionListProps[] = [];
  if (countryArray) {
    countryArray.map((country) => {
      const object: OptionListProps = {
        displayLabel: country?.attributes?.displayName,
        parentName: country?.attributes?.countryName,
        parentCode: country?.attributes?.countryCode,
        type: country?.attributes?.countryCode,
      };
      modifyCountryList.push(object);
    });
  }

  return modifyCountryList;
}

export function checkSupportSectionEnablePolicy(
  policyType: string,
  selectedToCountry: string | undefined,
  tagSearchField: string | undefined
) {
  let status = false;
  if (!selectedToCountry && !tagSearchField) {
    if (policyType === SingleTrip) {
      status = true;
    }
    if (policyType === NotSure) {
      status = true;
    }
  }

  return status;
}

export function getQuestionTwoState(
  toLocation: string | undefined,
  policyType: string | undefined,
  countryList: STCountryArray | undefined
) {
  let objectModified = {};

  if (toLocation && policyType === AnnualMultiTrip) {
    const selectedToCountry = toLocation.split("_")[0];

    if (!selectedToCountry) {
      objectModified = {
        toCountrySelected: "Uk only",
        valueSelected: toLocation,
        countryArray: [],
      };
    } else {
      objectModified = {
        toCountrySelected: selectedToCountry,
        valueSelected: toLocation,
        countryArray: [],
      };
    }
  } else {
    const countryArray: OptionListProps[] = [];
    const countryArry = toLocation?.split(",");
    if (countryList && countryList.length > 0) {
      countryArry?.map((country: string, index: number) => {
        const object: OptionListProps = {
          displayLabel: countryList?.[index]?.attributes?.countryName || "",
          parentName: countryList?.[index]?.attributes?.countryName,
          parentCode: countryList?.[index]?.attributes?.countryCode,
          type: "Country",
        };
        countryArray.push(object);
      });
      objectModified = {
        toCountrySelected: null,
        valueSelected: null,
        countryArray,
      };
    }
  }

  return objectModified;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findDuplicatesByProperty<T extends { [key: string]: any }>(
  arr: T[],
  property: keyof T
): T[] {
  const seen: { [key: string]: boolean } = {};
  const duplicates: T[] = [];

  arr.forEach((obj) => {
    const key = obj[property];
    if (seen[key]) {
      duplicates.push(obj);
    } else {
      seen[key] = true;
    }
  });

  return duplicates;
}

export function findFCDOCountry(value: string | undefined) {
  return fcdoCountryList.find((country: string) => country === value);
}

export function checkArrayContainString(array: [] = []) {
  let containsString = false;

  for (let i = 0; i < array.length; i++) {
    if (typeof array[i] === "string") {
      containsString = true;
      break;
    }
  }

  return containsString;
}

const brandImpact = {
  staysure: true,
  avanti: true,
  expat: false,
};

const toLocationSelectionBrandImpact = brandImpact[brand];

export function findFCDOCountries(
  value: string | undefined,
  restrictedCountries: RestrictedCountry[]
) {
  if (!value) return undefined;
  return restrictedCountries.find((country) => country.countryCode === value);
}

/**
 * Find a custom FCDO message for a specific country code from CMS data
 * @param countryCode - ISO country code (e.g., "IL", "SO")
 * @param customFcdoMessages - Object containing custom FCDO messages from CMS
 * @returns The custom FCDO message item if found, undefined otherwise
 */
export function findCustomFcdoMessage(
  countryCode: string | undefined,
  customFcdoMessages:
    | Record<
        string,
        {
          header: string;
          description: string;
          ctaLabel: string;
          country: string;
        }
      >
    | undefined
) {
  if (!countryCode || !customFcdoMessages) return undefined;

  return Object.values(customFcdoMessages).find(
    (message) => message.country === countryCode
  );
}

export default toLocationSelectionBrandImpact;
